// 英語の文章 (下訳)。名台詞は原文を見せ、その下にいまの英語でわかりやすく言い直したものを添える

import type { ContentText } from "./types";

export const en: ContentText = {
  locale: "en",

  // front / back は「In public, you are the King: you ...」の「you」の後に続く形
  types: {
    king: {
      label: "King",
      front: "stand before everyone, make the call, and carry the weight",
      back: "hold the helm of your own life and hand it to no one",
    },
    lover: {
      label: "Lover",
      front: "stay close to how people feel and warm everyone around you",
      back: "burn with longing for one single person",
    },
    fool: {
      label: "Fool",
      front: "loosen the air with laughter and light up the room",
      back: "make yourself giggle with the silliest daydreams",
    },
    schemer: {
      label: "Schemer",
      front: "take in the whole picture and quietly read ahead",
      back: "secretly plot your next moves",
    },
    warrior: {
      label: "Warrior",
      front: "step forward without hesitating and take on what's hard",
      back: "burn to do what no one has ever done",
    },
    dreamer: {
      label: "Dreamer",
      front: "see an ideal beyond the reality in front of you",
      back: "picture a future no one has seen yet, and feel both afraid and on fire",
    },
  },

  questions: [
    {
      text: "A meeting is split right down the middle.",
      choices: [
        "“I'll make the call here,” you say, and settle it",
        "“Let's all laugh for a second,” you say, and ease the tension",
        "You quietly take in the whole room and look for common ground",
      ],
    },
    {
      text: "Your first day on a new team.",
      choices: [
        "You talk to everyone first and end up at the center of the circle",
        "You break the ice with humor while watching how people relate",
        "You watch from the corner, looking for what only you can do",
      ],
    },
    {
      text: "The deadline is impossible, any way you look at it.",
      choices: [
        "You laugh, “We'll make it work!” and carry everyone along",
        "You think about the person who asked and talk to them honestly",
        "You rework the plan and charge ahead, all-nighter if needed",
      ],
    },
    {
      text: "Someone says something plainly wrong in front of everyone.",
      choices: [
        "You smoothly change the subject to keep the peace",
        "You stand up for whoever was hurt and push back right away",
        "You roll your eyes inside and quietly watch",
      ],
    },
    {
      text: "Someone praises you.",
      choices: [
        "You take it gracefully and share an even bigger dream",
        "You praise them right back",
        "You laugh it off, embarrassed, and secretly vow to do even better",
      ],
    },
    {
      text: "The night before a big presentation.",
      choices: [
        "You tell yourself, “I'm the lead,” and sleep soundly",
        "You picture the audience's faces and fine-tune how you'll speak",
        "You write out likely questions and rehearse in your head again and again",
      ],
    },
    {
      text: "A whole free day, and no one asking about your plans.",
      choices: [
        "Gather friends and run the day on your own plan",
        "Sink into books or films alone, collecting notes on human nature",
        "Head off on a whim to somewhere you've always wanted to go",
      ],
    },
    {
      text: "A dream you've never told anyone.",
      choices: [
        "To be the one in the middle who makes everyone smile",
        "To truly understand one person, deeply",
        "To achieve something no one has ever done",
      ],
    },
    {
      text: "What you can't stop thinking about on sleepless nights.",
      choices: [
        "Your strategy for the rest of your life",
        "Deciding that tomorrow you'll finally say what you couldn't",
        "Pointless daydreams that just won't stop",
      ],
    },
    {
      text: "When you lose something precious.",
      choices: [
        "You think about what it meant and rethink how you want to live",
        "You try to win back what you felt for it, in a new form",
        "You cry, you laugh, and above all you keep moving",
      ],
    },
    {
      text: "You're lost in a forest.",
      choices: [
        "Climb somewhere high and choose the way forward",
        "Walk along, talking with whoever you meet",
        "Enjoy being lost, and draw a map as you go",
      ],
    },
    {
      text: "The curtain falls on the final scene. How do you want to be?",
      choices: [
        "At center stage, surrounded by the people I love",
        "Leaving with one last line that makes the audience laugh",
        "Slipping quietly into the wings, with the look of someone who did it",
      ],
    },
  ],

  // word は配役の文の「Your two faces, and the ___ they make together」(同じタイプなら「That ___, the same in both faces」) に入る
  roles: {
    henry5: {
      name: "Henry V",
      play: "Henry V",
      word: "steadiness",
      intro:
        "The king who left his wild youth behind to lead England onto the battlefield. He speaks to his soldiers as one of them and binds their hearts together.",
      quote: "One more time into that gap in the wall, my friends, one more time!",
      costarReason:
        "Your old partner in mischief. Even after you're crowned, he reminds you how to drop the title and just laugh.",
    },
    cleopatra: {
      name: "Cleopatra",
      play: "Antony and Cleopatra",
      word: "shimmer",
      intro:
        "Queen of Egypt. She held on to both the pride of ruling a nation and her fierce love for the Roman general Antony, right to the end.",
      quote: "Something in me longs for what lasts forever.",
      costarReason: "Someone who answers your whims and theatrics with irony and humor. Never a dull moment.",
    },
    lear: {
      name: "King Lear",
      play: "King Lear",
      word: "range",
      intro:
        "The old king who divided his kingdom among his daughters and, wandering a stormy heath, came to see true love and his true self.",
      quote: "Is there anyone who can tell me who I really am?",
      costarReason:
        "Someone whose songs heal your heart after any storm, and who shows you what real love looks like.",
    },
    margaret: {
      name: "Queen Margaret",
      play: "Henry VI, Part 3",
      word: "force",
      intro:
        "The queen who led armies in place of her timid king. Stronger and more far-sighted than anyone, she fought her way through the Wars of the Roses.",
      quote: "Wise people don't sit and cry over what they've lost. They cheerfully look for a way to set it right.",
      costarReason: "A rival who never gives an inch. The harder you two compete, the sharper your edge becomes.",
    },
    katherine: {
      name: "Queen Katherine",
      play: "Henry VIII",
      word: "dignity",
      intro:
        "The queen who, even as the king pushed to cast her aside, stood firm in court and kept insisting she was in the right.",
      quote: "Your Majesty, I ask you to treat me fairly and justly.",
      costarReason: "Someone who understands your dignity and stands beside you without giving an inch.",
    },
    richard2: {
      name: "Richard II",
      play: "Richard II",
      word: "poetry",
      intro:
        "The king who spoke more beautifully than anyone about being king. Only when he lost the crown did he look at himself as just a human being.",
      quote: "For heaven's sake, let's sit on the ground and tell sad stories about how kings die.",
      costarReason: "Someone who listens to your poetry and stays close, wavering just as you do.",
    },
    hermione: {
      name: "Hermione",
      play: "The Winter's Tale",
      word: "quiet strength",
      intro:
        "The queen accused by a king mad with jealousy, who defended the truth without losing her grace. Sixteen years later, she returns as if by a miracle.",
      quote: "I'm not someone who cries easily, the way people expect women to.",
      costarReason: "Someone saved by your quiet strength, whose guarded heart slowly opens again.",
    },
    juliet: {
      name: "Juliet",
      play: "Romeo and Juliet",
      word: "devotion",
      intro:
        "The thirteen-year-old who fell in love at first sight at a masked ball and chose love over the hatred between two families.",
      quote: "What does a name matter? A rose would smell just as sweet if we called it something else.",
      costarReason: "Someone who loves just as wholeheartedly, and cheers on your love more seriously than anyone.",
    },
    launce: {
      name: "Launce",
      play: "The Two Gentlemen of Verona",
      word: "charm",
      intro:
        "A servant who turns his one-sided love for his unfeeling dog, Crab, into a one-man comedy routine.",
      quote: "I'm telling you, my dog Crab is the coldest-hearted dog alive.",
      costarReason: "The partner who throws themselves completely into your one-man show.",
    },
    helena: {
      name: "Helena",
      play: "All's Well That Ends Well",
      word: "resilience",
      intro:
        "A doctor's daughter who refused to give up on a love above her station, and cleared her own path with wit and action.",
      quote: "The help we need is often inside us. We just give heaven the credit.",
      costarReason:
        "A rule-breaking ally who couldn't care less about rank, and cheers on your challenge at the top of their lungs.",
    },
    imogen: {
      name: "Imogen",
      play: "Cymbeline",
      word: "constancy",
      intro:
        "A princess who trusted her husband to the end and crossed harsh mountains disguised as a boy. Through every trial, she never let go of love.",
      quote: "All I can see is the road right in front of me. This way, that way, what comes next: it's all fog I can't see through.",
      costarReason: "Someone who shares your constancy and will face any trial at your side.",
    },
    cressida: {
      name: "Cressida",
      play: "Troilus and Cressida",
      word: "wavering",
      intro:
        "A woman who fell in love in the middle of a war and was torn away by fate. Caught between love and fear, she wavered more honestly than anyone.",
      quote: "People value what they haven't won yet far more than it's really worth.",
      costarReason: "Someone you can share a wavering heart with, turning it into beautiful words together.",
    },
    jackcade: {
      name: "Jack Cade",
      play: "Henry VI, Part 2",
      word: "audacity",
      intro: "The rebel leader whose gift for making people laugh let him lead a crowd all the way into London.",
      quote: "We're most in order when we're most out of order.",
      costarReason: "Someone who sees the seriousness behind your bravado and backs you up with brains.",
    },
    berowne: {
      name: "Berowne",
      play: "Love's Labour's Lost",
      word: "playful cover",
      intro:
        "A man of wit who swore off love for three years, then fell in love first. Behind the jokes, he believed in love more than anyone.",
      quote: "A lover's eyes are the real books, the real arts, the real schools. They show, hold and feed the whole world.",
      costarReason: "Someone who sees through your playful cover and answers with just as much wit.",
    },
    dromio: {
      name: "Dromio",
      play: "The Comedy of Errors",
      word: "sunny innocence",
      intro:
        "A servant with a long-lost twin. Mistaken for his identical brother, he gets swept up in chaos everywhere he goes.",
      quote: "Am I Dromio? Am I your servant? Am I even me?",
      costarReason: "The best comedy partner you could ask for. Put you two together and the audience roars.",
    },
    richard3: {
      name: "Richard III",
      play: "Richard III",
      word: "contrast",
      intro:
        "One of Shakespeare's greatest villains, so charming he talks straight to the audience while plotting his way to the throne behind a smile.",
      quote: "Our winter of discontent is over. This sun of York has turned it into a glorious summer.",
      costarReason:
        "Someone who sees through your schemes faster than anyone. The tougher they are, the more seriously you play.",
    },
    bastard: {
      name: "Philip the Bastard",
      play: "King John",
      word: "thrill",
      intro:
        "A king's son born outside marriage, who laughed at the powerful with irony and humor, yet fought for his country more bravely than anyone.",
      quote: "This England has never bowed at the feet of a conqueror, and it never will.",
      costarReason: "Someone who delights in your boldness and pulls you onto a bigger stage.",
    },
    falstaff: {
      name: "Falstaff",
      play: "Henry IV, Part 2",
      word: "humanity",
      intro:
        "A hard-drinking, tall-tale-telling old knight. As the prince's partner in mischief, he scattered laughter and life lessons wherever he went.",
      quote: "I'm not just witty myself. I make everyone around me witty too.",
      costarReason:
        "A partner who truly laughs at your jokes, and who will one day let you watch them take the great stage.",
    },
    shylock: {
      name: "Shylock",
      play: "The Merchant of Venice",
      word: "pride",
      intro:
        "A Jewish moneylender who endured prejudice to make his living, and tried to win back his trampled pride through the power of a contract.",
      quote: "If you stick us with a needle, don't we bleed too?",
      costarReason: "Someone who understands, better than anyone, the weight of the pride you've carried.",
    },
    rosalind: {
      name: "Rosalind",
      play: "As You Like It",
      word: "lightness",
      intro:
        "A woman of wit who fled to the Forest of Arden and, disguised as a young man, coached others in love while winning her own.",
      quote: "Over the years, men have died and worms have eaten them, but never from love.",
      costarReason: "Someone who also loved while hiding their true feelings. They understand without a word.",
    },
    puck: {
      name: "Puck",
      play: "A Midsummer Night's Dream",
      word: "playfulness",
      intro:
        "The mischief-maker who serves Oberon, king of the fairies. His love potion throws the lovers into chaos, then leads them to a happy ending.",
      quote: "Oh my, what fools these humans are!",
      costarReason: "Someone who notices the sharp eyes behind your pranks.",
    },
    iago: {
      name: "Iago",
      play: "Othello",
      word: "foresight",
      intro:
        "The officer everyone trusts as “honest Iago,” who bends people's hearts with nothing but words. Often called Shakespeare's greatest mystery.",
      quote: "I'm not what I seem to be.",
      costarReason:
        "Someone who laughs and sees right through any scheme. One of the few people you can take your mask off with.",
    },
    mrsford: {
      name: "Mistress Ford",
      play: "The Merry Wives of Windsor",
      word: "grit",
      intro:
        "A cheerful, clever wife who teamed up with a friend to teach the flirting Falstaff a lesson he wouldn't forget.",
      quote: "I'd find twenty cheating turtledoves before I'd find one faithful man.",
      costarReason:
        "Someone who reads ahead as well as you do. As a team, unbeatable. As an enemy, the toughest there is.",
    },
    viola: {
      name: "Viola",
      play: "Twelfth Night",
      word: "bittersweetness",
      intro:
        "Shipwrecked and disguised as a young man, she fell for the duke she served, and kept carrying his love messages while hiding her own heart.",
      quote: "Oh, Time, you'll have to untangle this, not me. This knot is too tight for me to undo!",
      costarReason: "Someone whose light-footed wit gives you hints for untangling your knotted feelings.",
    },
    coriolanus: {
      name: "Coriolanus",
      play: "Coriolanus",
      word: "integrity",
      intro: "Rome's greatest warrior, who refused to flatter the people and was driven out of his own city.",
      quote: "This isn't the only world there is.",
      costarReason: "Someone with the same pride that bows to no one. Even in silence, you understand each other.",
    },
    katherina: {
      name: "Katherina",
      play: "The Taming of the Shrew",
      word: "warmth",
      intro:
        "The feared “shrew” with a tongue no one could beat. But beneath that strength was a heart waiting for someone who could meet her head-on.",
      quote: "If I'm as quick-tempered as a wasp, you'd better watch out for my sting.",
      costarReason: "Someone who takes your blunt honesty head-on and fires right back.",
    },
    beatrice: {
      name: "Beatrice",
      play: "Much Ado About Nothing",
      word: "wit",
      intro:
        "A bright, outspoken woman no one can beat in a war of words, who slowly falls for the very man she's always arguing with.",
      quote: "I'd rather listen to my dog bark at a crow than hear a man swear he loves me.",
      costarReason: "The person you most love trading jokes with, who understands the truth beneath the banter.",
    },
    titus: {
      name: "Titus",
      play: "Titus Andronicus",
      word: "tenacity",
      intro:
        "An old general who fought all his life for Rome. After losing everything, he takes his revenge with terrifying cunning.",
      quote: "Foolish Lucius, don't you see? Rome is nothing but a wilderness full of tigers.",
      costarReason: "Someone who knows the pain of being trampled, and understands the grief beneath your anger.",
    },
    hotspur: {
      name: "Hotspur",
      play: "Henry IV, Part 1",
      word: "directness",
      intro:
        "A hot-blooded young warrior, true to his nickname, who is always first to ride out wherever honor calls.",
      quote: "Waking a lion gets my blood racing far more than chasing a rabbit!",
      costarReason:
        "Someone who fires back with as much heat as you have. The more you clash, the better you somehow get along.",
    },
    macbeth: {
      name: "Macbeth",
      play: "Macbeth",
      word: "tug-of-war",
      intro:
        "A fearless Scottish general. From the night he hears the witches' prophecy, ambition and fear begin to battle inside him.",
      quote: "Is that a dagger I see in front of me, with its handle turned toward my hand?",
      costarReason:
        "Someone who also stepped onto the battlefield led by “voices,” and who turns your doubt into the strength to believe.",
    },
    brutus: {
      name: "Brutus",
      play: "Julius Caesar",
      word: "nobility",
      intro:
        "The idealist who turned his blade on Caesar, whom he loved, to protect Rome's freedom, and lived by what he believed was right to the very end.",
      quote: "There's a right moment for everything people do. Catch the high tide, and it carries you to good fortune.",
      costarReason: "Two people who won't bend on what's right. They support your ideals with the power of words.",
    },
    marina: {
      name: "Marina",
      play: "Pericles",
      word: "purity",
      intro:
        "A princess born at sea in a storm. Tossed about by a strange fate, she still saved people with her songs and her pure heart.",
      quote: "I've never said a cruel word or done a cruel thing to any living creature.",
      costarReason: "Someone who truly needs your pure-hearted kindness.",
    },
    hamlet: {
      name: "Hamlet",
      play: "Hamlet",
      word: "depth",
      intro:
        "The Prince of Denmark who learns the truth about his father's death and, pretending to be mad, keeps wrestling with “to be, or not to be.”",
      quote: "To live, or not to live: that's the question.",
      costarReason: "Someone whose mischief tickles your overthinking mind, until your shoulders finally relax.",
    },
    isabella: {
      name: "Isabella",
      play: "Measure for Measure",
      word: "inner strength",
      intro:
        "A young woman about to enter a convent who, to save her brother's life, stood up to a powerful man with wit and words.",
      quote: "It's wonderful to have a giant's strength, but using it like a giant is what tyrants do.",
      costarReason: "Someone who deeply respects your inner strength.",
    },
    joan: {
      name: "Joan of Arc",
      play: "Henry VI, Part 1",
      word: "conviction",
      intro: "A shepherd girl who said she heard the voice of God and fought at the head of the French army.",
      quote: "Glory is like a ripple in water. It keeps spreading wider and wider until, stretched too thin, it fades into nothing.",
      costarReason: "Someone who understands both the strength of your conviction and the fear of doubting.",
    },
    timon: {
      name: "Timon",
      play: "Timon of Athens",
      word: "wholeheartedness",
      intro:
        "A rich man of Athens who gave and gave without limit. Betrayed by his friends, he went alone into the woods and kept asking what it means to be human.",
      quote: "I am Misanthropos: I hate humankind.",
      costarReason: "Someone who shows you that even after betrayal, you can trust people again.",
    },
  },

  prospero: {
    name: "Prospero",
    play: "The Tempest",
    quote: "We're made of the same stuff as dreams, and our little life ends wrapped in sleep.",
  },

  castingText(role, types) {
    const f = types[role.front];
    const b = types[role.back];
    if (role.front === role.back) {
      return [
        `In public and alone alike, you are the ${f.label}: you ${f.front}.`,
        `That ${role.word}, the same in both faces, is ${role.name}.`,
      ];
    }
    return [
      `In public, you are the ${f.label}: you ${f.front}.`,
      `But alone, you become the ${b.label}: you ${b.back}.`,
      `Your two faces, and the ${role.word} they make together: that is ${role.name}.`,
    ];
  },

  inPublic: (label) => `In public: ${label}`,
  alone: (label) => `Alone: ${label}`,
  playTitle: (play) => `from ${play}`,

  site: {
    title: "Bard Club | Your role in Shakespeare",
    description:
      "Twelve questions reveal who you are in public and who you are alone, then cast you as one of 36 characters from Shakespeare.",
  },

  share: {
    hashtags: ["BardClub", "Shakespeare"],
    title: (role) => `I was cast as ${role.name}`,
    text: (role, types) =>
      `I was cast as ${role.name} (${role.play})! In public: the ${types[role.front].label}. Alone: the ${types[role.back].label}. Who are you?`,
    description: (role, types) =>
      `In public: the ${types[role.front].label}. Alone: the ${types[role.back].label}. Answer 12 questions to find your role among 36 Shakespeare characters.`,
  },
};
